import React, { FC, useMemo } from "react";

import { faLock } from "@fortawesome/pro-regular-svg-icons/faLock";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tag from "antd/es/tag";
import cn from "classnames";
import { useIntl } from "react-intl";

import CardType from "@app/enum/dashboard/CardType";
import CardFeature from "@app/scene/dashboard/components/addEditCard/AddCardWizard/enum/CardFeature";
import hasFeature from "@app/scene/dashboard/components/addEditCard/AddCardWizard/utils/hasFeature";

import useCardInfo from "../utils/useCardInfo";

import CardItem, { CardItemProps } from "./CardItem";
import styles from "./CardList.module.scss";

const allAllowed = () => true;

export interface CardListProps {
  cardFilter?: (cardType: CardType) => boolean;
  className?: string;
  featureFilter?: CardFeature;
  isMileageTrackingEnabled?: boolean;
  onClick?: (cardType: CardType) => void;
  query?: string;
  selectedCard?: CardType;
}

const CardList: FC<CardListProps> = ({
  cardFilter = allAllowed,
  className,
  featureFilter,
  isMileageTrackingEnabled,
  onClick,
  query,
  selectedCard,
}) => {
  const intl = useIntl();
  const cardInfo = useCardInfo(intl);

  const visibleCards = useMemo(() => {
    const q = query?.trim().toLowerCase();
    return (Object.values(CardType) as CardType[])
      .filter(cardFilter)
      .filter((cardType) => hasFeature(cardType, featureFilter))
      .map((cardType) => ({ ...cardInfo[cardType], type: cardType } as CardItemProps))
      .filter((card) => !q || card.title.toLowerCase().includes(q));
  }, [cardInfo, query, featureFilter, cardFilter]);

  return (
    <div className={cn(styles.container, className)}>
      {visibleCards.map((card) => (
        <CardItem
          {...card}
          extra={
            !isMileageTrackingEnabled && card.type === CardType.MILEAGE_TRACKING ? (
              <Tag className={styles.titleTag}>
                {intl.formatMessage(
                  {
                    id: "dashboard.addCardWizard.selectTypeStep.card.mileageTracking.tag",
                    defaultMessage: "{icon} Requires Mileage Tracker",
                    description: "Tag to show on the MT card when MT is not enabled",
                  },
                  { icon: <FontAwesomeIcon icon={faLock} /> }
                )}
              </Tag>
            ) : null
          }
          key={card.type}
          onClick={onClick}
          selected={selectedCard === card.type}
        />
      ))}
    </div>
  );
};

export default CardList;
